import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Package2, PackageCheck, Edit, Trash2, MoreHorizontal, Eye } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ImageUpload } from '@/components/ui/image-upload';
import { ProductThumbnail } from '@/components/ui/product-thumbnail';
import { inventoryItemSchema as globalInventoryItemSchema, optionalQuantitySchema, optionalCurrencySchema } from '@/lib/validation';
import { useNumberInput, formatNumberForInput } from '@/hooks/useNumberInput';

// Local schemas for forms that map to database fields
const inventoryItemSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  sku: z.string().min(1, 'SKU is required'),
  type: z.enum(['raw_material', 'finished_product']),
  description: z.string().optional(),
  unit_of_measure: z.string().min(1, 'Unit of measure is required'),
  current_stock: z.number().min(0, 'Stock cannot be negative'),
  min_stock_level: optionalQuantitySchema,
  max_stock_level: optionalQuantitySchema,
  unit_cost: optionalCurrencySchema,
  selling_price: optionalCurrencySchema,
  image_url: z.string().optional(),
});

const receiveInventorySchema = z.object({
  inventory_item_id: z.string().min(1, 'Please select an inventory item'),
  quantity_received: z.number().min(0.01, 'Quantity must be greater than 0'),
  po_number: z.string().optional(),
  date_received: z.string().min(1, 'Date received is required'),
  supplier_name: z.string().optional(),
  unit_cost: optionalCurrencySchema,
  notes: z.string().optional(),
});

const adjustInventorySchema = z.object({
  inventory_item_id: z.string().min(1, 'Please select an inventory item'),
  adjustment_type: z.enum(['increase', 'decrease']),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  reason: z.string().min(1, 'Reason is required'),
  notes: z.string().optional(),
});

type InventoryItemForm = z.infer<typeof inventoryItemSchema>;
type ReceiveInventoryForm = z.infer<typeof receiveInventorySchema>;
type AdjustInventoryForm = z.infer<typeof adjustInventorySchema>;

export const InventoryItems = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('raw_material');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReceiveDialogOpen, setIsReceiveDialogOpen] = useState(false);
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [viewingItem, setViewingItem] = useState<any>(null);
  const queryClient = useQueryClient();
  
  // Check if user can view sensitive cost/price data
  const { hasPermission, isSuperAdmin, userRole } = usePermissions();
  const canViewSensitiveData = isSuperAdmin || 
    userRole === 'admin' || 
    userRole === 'manager' || 
    hasPermission(PERMISSIONS.MANAGE_INVENTORY);

  const form = useForm<InventoryItemForm>({
    resolver: zodResolver(inventoryItemSchema),
    defaultValues: {
      name: '',
      sku: '',
      type: 'raw_material',
      description: '',
      unit_of_measure: 'pieces',
      current_stock: 0,
      min_stock_level: undefined,
      max_stock_level: undefined,
      unit_cost: undefined,
      selling_price: undefined,
      image_url: undefined,
    },
  });

  const editForm = useForm<InventoryItemForm>({
    resolver: zodResolver(inventoryItemSchema),
  });

  const receiveForm = useForm<ReceiveInventoryForm>({
    resolver: zodResolver(receiveInventorySchema),
    defaultValues: {
      quantity_received: 0,
      date_received: new Date().toISOString().split('T')[0],
      unit_cost: 0,
    },
  });

  const adjustForm = useForm<AdjustInventoryForm>({
    resolver: zodResolver(adjustInventorySchema),
    defaultValues: {
      adjustment_type: 'increase',
      quantity: 0,
    },
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['inventory-items', canViewSensitiveData],
    queryFn: async () => {
      if (canViewSensitiveData) {
        // Admins and managers can access full data including costs and supplier info
        const { data, error } = await supabase
          .from('inventory_items')
          .select('*')
          .order('name');
        
        if (error) throw error;
        return data;
      } else {
        // Regular inventory staff get data without sensitive cost/supplier information
        const { data, error } = await supabase
          .rpc('get_inventory_items_basic')
          .order('name');
        
        if (error) throw error;
        return data;
      }
    },
  });

  const createItem = useMutation({
    mutationFn: async (data: InventoryItemForm) => {
      const { data: newItem, error } = await supabase
        .from('inventory_items')
        .insert(data as any)
        .select()
        .single();
      
      if (error) throw error;

      // Create initial stock transaction if current_stock > 0
      if (data.current_stock > 0) {
        const { error: transactionError } = await supabase
          .from('inventory_transactions')
          .insert([{
            inventory_item_id: newItem.id,
            transaction_type: 'adjusted',
            quantity: data.current_stock,
            reference_type: 'initial_stock',
            reference_id: null,
            notes: 'Initial stock when item was created',
            user_id: (await supabase.auth.getUser()).data.user?.id,
          }]);

        if (transactionError) throw transactionError;
      }

      return newItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      setIsDialogOpen(false);
      form.reset();
      toast.success('Item created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create item');
      console.error(error);
    },
  });

  const receiveInventory = useMutation({
    mutationFn: async (data: ReceiveInventoryForm) => {
      // Get current inventory item
      const { data: currentItem, error: fetchError } = await supabase
        .from('inventory_items')
        .select('current_stock')
        .eq('id', data.inventory_item_id)
        .maybeSingle();
      
      if (fetchError || !currentItem) throw fetchError || new Error('Inventory item not found');

      // Update inventory item stock
      const { error: updateError } = await supabase
        .from('inventory_items')
        .update({ 
          current_stock: currentItem.current_stock + data.quantity_received 
        })
        .eq('id', data.inventory_item_id);

      if (updateError) throw updateError;

      // Create inventory transaction record
      const { error: transactionError } = await supabase
        .from('inventory_transactions')
        .insert([{
          inventory_item_id: data.inventory_item_id,
          transaction_type: 'received',
          quantity: data.quantity_received,
          unit_cost: data.unit_cost,
          reference_type: 'purchase_order',
          reference_id: null, // Could be linked to PO system later
          notes: `PO: ${data.po_number || 'N/A'}, Supplier: ${data.supplier_name || 'N/A'}, Received: ${data.date_received}. ${data.notes || ''}`.trim(),
          user_id: (await supabase.auth.getUser()).data.user?.id,
        }]);

      if (transactionError) throw transactionError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      setIsReceiveDialogOpen(false);
      receiveForm.reset();
      toast.success('Inventory received successfully');
    },
    onError: (error) => {
      toast.error('Failed to receive inventory');
      console.error(error);
    },
  });

  const adjustInventory = useMutation({
    mutationFn: async (data: AdjustInventoryForm) => {
      // Get current inventory item
      const { data: currentItem, error: fetchError } = await supabase
        .from('inventory_items')
        .select('current_stock')
        .eq('id', data.inventory_item_id)
        .maybeSingle();
      
      if (fetchError || !currentItem) throw fetchError || new Error('Inventory item not found');

      // Calculate new stock
      const adjustmentQuantity = data.adjustment_type === 'increase' ? data.quantity : -data.quantity;
      const newStock = currentItem.current_stock + adjustmentQuantity;

      if (newStock < 0) {
        throw new Error('Adjustment would result in negative stock');
      }

      // Update inventory item stock
      const { error: updateError } = await supabase
        .from('inventory_items')
        .update({ 
          current_stock: newStock 
        })
        .eq('id', data.inventory_item_id);

      if (updateError) throw updateError;

      // Create inventory transaction record
      const { error: transactionError } = await supabase
        .from('inventory_transactions')
        .insert([{
          inventory_item_id: data.inventory_item_id,
          transaction_type: 'adjusted',
          quantity: adjustmentQuantity, // Positive for increase, negative for decrease
          reference_type: 'manual_adjustment',
          reference_id: null,
          notes: `Manual adjustment: ${data.reason}. ${data.notes || ''}`.trim(),
          user_id: (await supabase.auth.getUser()).data.user?.id,
        }]);

      if (transactionError) throw transactionError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
      setIsAdjustDialogOpen(false);
      adjustForm.reset();
      toast.success('Inventory adjusted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to adjust inventory: ${error.message || 'Unknown error'}`);
      console.error(error);
    },
  });

  const updateItem = useMutation({
    mutationFn: async (data: InventoryItemForm & { id: string }) => {
      const { id, ...updateData } = data;
      const { data: updatedItem, error } = await supabase
        .from('inventory_items')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return updatedItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      setIsEditDialogOpen(false);
      setEditingItem(null);
      editForm.reset();
      toast.success('Item updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update item');
      console.error(error);
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (itemId: string) => {
      // Check if item has any transactions
      const { data: transactions, error: transactionError } = await supabase
        .from('inventory_transactions')
        .select('id')
        .eq('inventory_item_id', itemId)
        .limit(1);

      if (transactionError) throw transactionError;

      if (transactions && transactions.length > 0) {
        throw new Error('Cannot delete item with existing transactions. Consider archiving instead.');
      }

      const { error } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
      toast.success('Item deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete item: ${error.message || 'Unknown error'}`);
      console.error(error);
    },
  });

  const handleEditItem = (item: any) => {
    setEditingItem(item);
    editForm.reset({
      name: item.name,
      sku: item.sku,
      type: item.type,
      description: item.description || '',
      unit_of_measure: item.unit_of_measure,
      current_stock: item.current_stock,
      min_stock_level: item.min_stock_level,
      max_stock_level: item.max_stock_level,
      unit_cost: item.unit_cost,
      selling_price: item.selling_price,
      image_url: item.image_url || undefined,
    });
    setIsEditDialogOpen(true);
  };

  const handleViewItem = (item: any) => {
    setViewingItem(item);
    setIsDetailDialogOpen(true);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.sku || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStockStatus = (item: any) => {
    if (item.current_stock <= (item.min_stock_level || 0)) {
      return <Badge variant="destructive">Low Stock</Badge>;
    }
    if (item.max_stock_level && item.current_stock >= item.max_stock_level) {
      return <Badge variant="secondary">High Stock</Badge>;
    }
    return <Badge variant="default">In Stock</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package2 className="h-5 w-5" />
              Inventory Items
            </CardTitle>
            <CardDescription>
              Manage raw materials and finished products
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Dialog open={isAdjustDialogOpen} onOpenChange={setIsAdjustDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Adjust Inventory</DialogTitle>
                </DialogHeader>
                <Form {...adjustForm}>
                  <form onSubmit={adjustForm.handleSubmit((data) => adjustInventory.mutate(data))} className="space-y-4">
                    <FormField
                      control={adjustForm.control}
                      name="inventory_item_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Inventory Item</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select item" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {items.map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name} ({item.sku}) - Current: {item.current_stock}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={adjustForm.control}
                        name="adjustment_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Adjustment Type</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="increase">Increase Stock</SelectItem>
                                <SelectItem value="decrease">Decrease Stock</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={adjustForm.control}
                        name="quantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity</FormLabel>
                            <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 placeholder="0"
                                 {...field}
                                 onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                               />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={adjustForm.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reason</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select reason" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="count_adjustment">Physical Count Adjustment</SelectItem>
                              <SelectItem value="damaged_goods">Damaged Goods</SelectItem>
                              <SelectItem value="expired_goods">Expired Goods</SelectItem>
                              <SelectItem value="theft_loss">Theft/Loss</SelectItem>
                              <SelectItem value="found_inventory">Found Inventory</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={adjustForm.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Notes</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Additional details about this adjustment" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsAdjustDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={adjustInventory.isPending}>
                        {adjustInventory.isPending ? 'Processing...' : 'Adjust Inventory'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
            <Dialog open={isReceiveDialogOpen} onOpenChange={setIsReceiveDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full sm:w-auto">
                  <PackageCheck className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Receive Inventory</span>
                  <span className="sm:hidden">Receive</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Receive Inventory</DialogTitle>
                </DialogHeader>
                <Form {...receiveForm}>
                  <form onSubmit={receiveForm.handleSubmit((data) => receiveInventory.mutate(data))} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={receiveForm.control}
                        name="inventory_item_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Inventory Item</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select item" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {items.map((item) => (
                                  <SelectItem key={item.id} value={item.id}>
                                    {item.name} ({item.sku})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={receiveForm.control}
                        name="quantity_received"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity Received</FormLabel>
                            <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 placeholder="0"
                                 {...field}
                                 onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                               />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={receiveForm.control}
                        name="po_number"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>P.O. Number</FormLabel>
                            <FormControl>
                              <Input placeholder="Purchase order number" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={receiveForm.control}
                        name="date_received"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date Received</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={receiveForm.control}
                        name="supplier_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Supplier Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Supplier name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={receiveForm.control}
                        name="unit_cost"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Unit Cost</FormLabel>
                            <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 placeholder="0.00"
                                 {...field}
                                 onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                               />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={receiveForm.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Additional notes about this receipt" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsReceiveDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={receiveInventory.isPending}>
                        {receiveInventory.isPending ? 'Processing...' : 'Receive Inventory'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Add Item</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Inventory Item</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit((data) => createItem.mutate(data))} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Item name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="sku"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SKU</FormLabel>
                          <FormControl>
                            <Input placeholder="SKU" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Type</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="raw_material">Raw Material</SelectItem>
                              <SelectItem value="finished_product">Finished Product</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="unit_of_measure"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Unit of Measure</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select unit" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="pieces">Pieces</SelectItem>
                              <SelectItem value="units">Units</SelectItem>
                              <SelectItem value="grams">Grams</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Item description" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="current_stock"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Stock</FormLabel>
                           <FormControl>
                             <Input 
                               type="number" 
                               step="0.01"
                               {...field}
                               onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                             />
                           </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                     <FormField
                       control={form.control}
                       name="min_stock_level"
                       render={({ field }) => {
                         const { handleChange } = useNumberInput({ 
                           onChange: field.onChange, 
                           allowEmpty: true, 
                           allowZero: false 
                         });
                         return (
                           <FormItem>
                             <FormLabel>Min Stock Level</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 value={formatNumberForInput(field.value)}
                                 onChange={handleChange}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         );
                       }}
                     />
                     <FormField
                       control={form.control}
                       name="max_stock_level"
                       render={({ field }) => {
                         const { handleChange } = useNumberInput({ 
                           onChange: field.onChange, 
                           allowEmpty: true, 
                           allowZero: false 
                         });
                         return (
                           <FormItem>
                             <FormLabel>Max Stock Level</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 value={formatNumberForInput(field.value)}
                                 onChange={handleChange}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         );
                       }}
                     />
                  </div>

                   <div className="grid grid-cols-2 gap-4">
                     <FormField
                       control={form.control}
                       name="unit_cost"
                       render={({ field }) => {
                         const { handleChange } = useNumberInput({ 
                           onChange: field.onChange, 
                           allowEmpty: true, 
                           allowZero: true 
                         });
                         return (
                           <FormItem>
                             <FormLabel>Unit Cost</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 value={formatNumberForInput(field.value)}
                                 onChange={handleChange}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         );
                       }}
                     />
                     <FormField
                       control={form.control}
                       name="selling_price"
                       render={({ field }) => {
                         const { handleChange } = useNumberInput({ 
                           onChange: field.onChange, 
                           allowEmpty: true, 
                           allowZero: true 
                         });
                         return (
                           <FormItem>
                             <FormLabel>Selling Price</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 value={formatNumberForInput(field.value)}
                                 onChange={handleChange}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         );
                       }}
                     />
                   </div>

                  <FormField
                    control={form.control}
                    name="image_url"
                    render={({ field }) => (
                      <FormItem>
                        <ImageUpload
                          value={field.value}
                          onChange={(url) => field.onChange(url)}
                          bucket="product-images"
                          disabled={createItem.isPending}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createItem.isPending}>
                      {createItem.isPending ? 'Creating...' : 'Create Item'}
                    </Button>
                  </div>
                </form>
              </Form>
              </DialogContent>
            </Dialog>
            
            {/* Edit Item Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Edit Inventory Item</DialogTitle>
                </DialogHeader>
                <Form {...editForm}>
                  <form onSubmit={editForm.handleSubmit((data) => updateItem.mutate({ ...data, id: editingItem?.id }))} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={editForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Item name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editForm.control}
                        name="sku"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>SKU</FormLabel>
                            <FormControl>
                              <Input placeholder="SKU" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={editForm.control}
                        name="type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="raw_material">Raw Material</SelectItem>
                                <SelectItem value="finished_product">Finished Product</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={editForm.control}
                        name="unit_of_measure"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Unit of Measure</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="pieces">Pieces</SelectItem>
                                <SelectItem value="units">Units</SelectItem>
                                <SelectItem value="grams">Grams</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={editForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Item description" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={editForm.control}
                        name="current_stock"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Current Stock</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 {...field}
                                 onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                               />
                             </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                       <FormField
                         control={editForm.control}
                         name="min_stock_level"
                         render={({ field }) => (
                           <FormItem>
                             <FormLabel>Min Stock Level</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 {...field}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   field.onChange(value === '' ? undefined : parseFloat(value) || undefined);
                                 }}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         )}
                       />
                       <FormField
                         control={editForm.control}
                         name="max_stock_level"
                         render={({ field }) => (
                           <FormItem>
                             <FormLabel>Max Stock Level</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 {...field}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   field.onChange(value === '' ? undefined : parseFloat(value) || undefined);
                                 }}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         )}
                       />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                       <FormField
                         control={editForm.control}
                         name="unit_cost"
                         render={({ field }) => (
                           <FormItem>
                             <FormLabel>Unit Cost</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 {...field}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   field.onChange(value === '' ? undefined : parseFloat(value) || undefined);
                                 }}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         )}
                       />
                       <FormField
                         control={editForm.control}
                         name="selling_price"
                         render={({ field }) => (
                           <FormItem>
                             <FormLabel>Selling Price</FormLabel>
                             <FormControl>
                               <Input 
                                 type="number" 
                                 step="0.01"
                                 {...field}
                                 onChange={(e) => {
                                   const value = e.target.value;
                                   field.onChange(value === '' ? undefined : parseFloat(value) || undefined);
                                 }}
                               />
                             </FormControl>
                             <FormMessage />
                           </FormItem>
                         )}
                       />
                    </div>

                    <FormField
                      control={editForm.control}
                      name="image_url"
                      render={({ field }) => (
                        <FormItem>
                          <ImageUpload
                            value={field.value}
                            onChange={(url) => field.onChange(url)}
                            bucket="product-images"
                            disabled={updateItem.isPending}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={updateItem.isPending}>
                        {updateItem.isPending ? 'Updating...' : 'Update Item'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex rounded-lg border border-input bg-background">
            <Button 
              variant={typeFilter === 'raw_material' ? 'default' : 'ghost'}
              onClick={() => setTypeFilter('raw_material')}
              className="rounded-r-none border-r"
              size="sm"
            >
              Raw Materials
            </Button>
            <Button 
              variant={typeFilter === 'finished_product' ? 'default' : 'ghost'}
              onClick={() => setTypeFilter('finished_product')}
              className="rounded-none border-r"
              size="sm"
            >
              Finished Products
            </Button>
            <Button 
              variant={typeFilter === 'all' ? 'default' : 'ghost'}
              onClick={() => setTypeFilter('all')}
              className="rounded-l-none"
              size="sm"
            >
              All Items
            </Button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Total Stock</TableHead>
              <TableHead>Committed</TableHead>
              <TableHead>Available</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Status</TableHead>
              {canViewSensitiveData && <TableHead>Unit Cost</TableHead>}
              {canViewSensitiveData && <TableHead>Selling Price</TableHead>}
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center">No items found</TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow 
                  key={item.id} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleViewItem(item)}
                >
                  <TableCell>
                    <ProductThumbnail 
                      imageUrl={item.image_url} 
                      name={item.name} 
                      size="md"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>
                    <Badge variant={item.type === 'raw_material' ? 'secondary' : 'default'}>
                      {item.type === 'raw_material' ? 'Raw Material' : 'Finished Product'}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.current_stock}</TableCell>
                  <TableCell>{item.committed_stock || 0}</TableCell>
                  <TableCell>{(item.current_stock - (item.committed_stock || 0))}</TableCell>
                  <TableCell>{item.unit_of_measure}</TableCell>
                  <TableCell>{getStockStatus(item)}</TableCell>
                  {canViewSensitiveData && <TableCell>${(item as any).unit_cost?.toFixed(2) || 'N/A'}</TableCell>}
                  {canViewSensitiveData && <TableCell>${(item as any).selling_price?.toFixed(2) || 'N/A'}</TableCell>}
                   <TableCell onClick={(e) => e.stopPropagation()}>
                     <DropdownMenu>
                       <DropdownMenuTrigger asChild>
                         <Button variant="ghost" className="h-8 w-8 p-0">
                           <span className="sr-only">Open menu</span>
                           <MoreHorizontal className="h-4 w-4" />
                         </Button>
                       </DropdownMenuTrigger>
                       <DropdownMenuContent align="end">
                         <DropdownMenuItem onClick={() => handleViewItem(item)}>
                           <Eye className="mr-2 h-4 w-4" />
                           View Details
                         </DropdownMenuItem>
                         <DropdownMenuItem onClick={() => handleEditItem(item)}>
                           <Edit className="mr-2 h-4 w-4" />
                           Edit Item
                         </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete Item
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the inventory item "{item.name}".
                                Items with existing transactions cannot be deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteItem.mutate(item.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Item Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {viewingItem && (
            <div className="space-y-6">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <ProductThumbnail 
                    imageUrl={viewingItem.image_url} 
                    name={viewingItem.name} 
                    size="lg"
                    className="w-32 h-32"
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold">{viewingItem.name}</h3>
                    <p className="text-muted-foreground">SKU: {viewingItem.sku}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Type</label>
                      <p className="mt-1">
                        <Badge variant={viewingItem.type === 'raw_material' ? 'secondary' : 'default'}>
                          {viewingItem.type === 'raw_material' ? 'Raw Material' : 'Finished Product'}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <p className="mt-1">{getStockStatus(viewingItem)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {viewingItem.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="mt-1 text-sm">{viewingItem.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Current Stock</label>
                  <p className="mt-1 text-lg font-semibold">{viewingItem.current_stock}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Committed</label>
                  <p className="mt-1 text-lg">{viewingItem.committed_stock || 0}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Available</label>
                  <p className="mt-1 text-lg font-semibold">{viewingItem.current_stock - (viewingItem.committed_stock || 0)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Unit</label>
                  <p className="mt-1">{viewingItem.unit_of_measure}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Min Stock Level</label>
                  <p className="mt-1">{viewingItem.min_stock_level || 'Not set'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Max Stock Level</label>
                  <p className="mt-1">{viewingItem.max_stock_level || 'Not set'}</p>
                </div>
              </div>

              {canViewSensitiveData && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Unit Cost</label>
                    <p className="mt-1 text-lg">${(viewingItem as any).unit_cost?.toFixed(2) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Selling Price</label>
                    <p className="mt-1 text-lg">${(viewingItem as any).selling_price?.toFixed(2) || 'N/A'}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
                  Close
                </Button>
                <Button type="button" onClick={() => {
                  setIsDetailDialogOpen(false);
                  handleEditItem(viewingItem);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Item
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};